import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
// Importing the worker entry shim causes pdf.js to run its worker code
// inline on the main thread (sets window.pdfjsWorker) rather than loading
// a separate file via the Web Worker API.  This means no external
// pdf.worker.min.js is required — the whole PDF engine is self-contained
// inside pma.ui.js, which works on air-gapped / local-network installations.
import "pdfjs-dist/legacy/build/pdf.worker.entry";
import { Events, Themes } from "./definitions";

// Local DOM helpers (mirrors the exported versions in viewportHelpers.js)
function hasClass(element, className) {
    return (" " + element.className + " ").indexOf(" " + className + " ") !== -1;
}

function addClass(element, className) {
    if (!element) {
        return;
    }

    if (hasClass(element, className)) {
        return;
    }

    element.className += " " + className;
}

function removeClass(element, className) {
    if (!element || !hasClass(element, className)) {
        return;
    }

    element.className = element.className.replace(new RegExp(className, "g"), "");
}

// ─── PDF support ─────────────────────────────────────────────────────────────

/**
 * Returns true if the given imageInfo describes a PDF file.
 * @param {Object} imageInfo
 * @returns {boolean}
 */
export function isPdf(imageInfo) {
    if (!imageInfo) {
        return false;
    }

    if (imageInfo.MetaData && imageInfo.MetaData.length > 0) {
        const fileFormatMeta = imageInfo.MetaData.find(x => x.Name.toLowerCase() === "fileformat");
        if (fileFormatMeta && fileFormatMeta.Value && fileFormatMeta.Value.toLowerCase() === "pdf") {
            return true;
        }
    }

    if (imageInfo.Filename && imageInfo.Filename.toLowerCase().endsWith(".pdf")) {
        return true;
    }

    return false;
}

/**
 * Tears down the PDF viewer for this viewport: cancels any in-progress render
 * tasks, destroys the PDF.js document, removes the PDF container from the DOM,
 * and nulls out all PDF-related state fields.
 * Called with `this` bound to a Viewport instance.
 */
export function cleanupPdfViewer() {
    // Cancel single-page render task
    if (this._pdfRenderTask) {
        try {
            this._pdfRenderTask.cancel();
        } catch (e) {
            /* ignore */
        }
        
        this._pdfRenderTask = null;
    }

    // Cancel per-page render tasks (continuous mode)
    if (this._pdfPageCanvases) {
        this._pdfPageCanvases.forEach(function (_canvas, pageNum) {
            var taskKey = "_pdfRenderTask_p" + pageNum;
            if (this[taskKey]) {
                try {
                    this[taskKey].cancel();
                } catch (e) {
                    /* ignore */
                }

                this[taskKey] = null;
            }
        }, this);
    }

    // Destroy the PDF.js document
    if (this.pdfDoc) {
        try {
            this.pdfDoc.destroy();
        } catch (e) {
            /* ignore */
        }

        this.pdfDoc = null;
    }

    // Remove PDF container from the DOM
    if (this.pdfContainer && this.pdfContainer.parentNode) {
        this.pdfContainer.parentNode.removeChild(this.pdfContainer);
    }

    this.pdfContainer = null;
    this.pdfCanvas = null;
    if (this._pdfPageCanvases) {
        this._pdfPageCanvases.clear();
    }

    if (this._pdfPageWrappers) {
        this._pdfPageWrappers.clear();
    }

    // Remove PDF UI controls from the viewport element
    if (this.element) {
        var controlSelectors = [
            ".pma-ui-pdf-navigation-controls",
            ".pma-ui-pdf-topbar",
            ".pma-ui-pdf-filename",
        ];

        controlSelectors.forEach(function (sel) {
            var el = this.element.querySelector(sel);
            if (el && el.parentNode) {
                el.parentNode.removeChild(el);
            }
        }, this);
    }

    this.pdfFilenameBar = null;
}

/**
 * Builds the URL that serves the raw PDF bytes from PMA.core.
 * Called with `this` bound to a Viewport instance.
 * @returns {string}
 */
export function getPdfUrl() {
    return (
        this.imagesUrl +
        "transfer/Download/File?sessionID=" +
        encodeURIComponent(this.sessionID) +
        "&path=" +
        encodeURIComponent(this.image)
    );
}

/**
 * Renders one PDF page onto a canvas.
 * Called with `this` bound to a Viewport instance.
 * @param {number} pageNum - 1-based page number
 * @param {function} [callback] - Called after the page has been rendered
 * @param {HTMLCanvasElement} [targetCanvas] - Canvas to render into (defaults to this.pdfCanvas for single-page mode)
 */
export function renderPdfPage(pageNum, callback, targetCanvas) {
    const self = this;
    if (!self.pdfDoc) {
        return;
    }

    const canvas = targetCanvas || self.pdfCanvas;
    if (!canvas) {
        return;
    }

    // Cancel any in-flight render task for this canvas/page
    const taskKey = targetCanvas ? "_pdfRenderTask_p" + pageNum : "_pdfRenderTask";
    if (self[taskKey]) {
        self[taskKey].cancel();
        self[taskKey] = null;
    }

    self.pdfDoc
        .getPage(pageNum)
        .then(function (page) {
            const containerWidth = self.pdfContainer ? Math.max(self.pdfContainer.clientWidth - 32, 100) : 800;
            const unscaledViewport = page.getViewport({ scale: 1 });
            const zoomFactor = self._pdfZoomFactor != null ? self._pdfZoomFactor : 1.0;
            const scale = (containerWidth / unscaledViewport.width) * zoomFactor;
            const scaledViewport = page.getViewport({ scale: scale });

            self._pdfScale = scale;

            const context = canvas.getContext("2d");
            canvas.width = scaledViewport.width;
            canvas.height = scaledViewport.height;

            // Keep the wrapper dimensions in sync with the canvas so the
            // scrollbar height stays accurate after a zoom change.
            const wrapper = canvas.parentElement;
            if (wrapper && wrapper.classList.contains("pma-ui-pdf-page-wrapper")) {
                wrapper.style.width = scaledViewport.width + "px";
                wrapper.style.minHeight = scaledViewport.height + "px";
            }

            // Update page info label only in single-page mode (no targetCanvas)
            if (!targetCanvas && self.pdfPageInfo) {
                self.pdfPageInfo.textContent = pageNum + " / " + self.pdfDoc.numPages;
            }

            const renderTask = page.render({
                canvasContext: context,
                viewport: scaledViewport,
            });

            self[taskKey] = renderTask;

            renderTask.promise
                .then(function () {
                    self[taskKey] = null;
                    if (typeof callback === "function") {
                        callback();
                    }
                })
                .catch(function (err) {
                    if (err && err.name !== "RenderingCancelledException") {
                        console.error("PDF render error:", err);
                    }
                });
        })
        .catch(function (err) {
            console.error("PDF getPage error:", err);
        });
}

/**
 * Re-renders all page canvases at the current zoom level (continuous scroll mode).
 * Also updates every page wrapper's placeholder dimensions so the scrollbar
 * height stays accurate even for pages not yet rendered.
 * Called with `this` bound to a Viewport instance.
 */
export function refreshPdfPages() {
    const self = this;
    if (!self.pdfDoc || self._pdfScrollMode !== "continuous" || !self._pdfPageCanvases) {
        return;
    }

    const containerWidth = self.pdfContainer ? Math.max(self.pdfContainer.clientWidth - 32, 100) : 800;
    const zoomFactor = self._pdfZoomFactor != null ? self._pdfZoomFactor : 1.0;

    self._pdfPageWrappers.forEach(function (wrapper, pageNum) {
        // Update placeholder dimensions eagerly (no rendering needed).
        self.pdfDoc.getPage(pageNum).then(function (page) {
            const uv = page.getViewport({ scale: 1 });
            const scale = (containerWidth / uv.width) * zoomFactor;
            const sv = page.getViewport({ scale: scale });
            wrapper.style.width = sv.width + "px";
            wrapper.style.minHeight = sv.height + "px";
        });

        // Re-render canvas content at the new zoom.
        const canvas = self._pdfPageCanvases.get(pageNum);
        if (canvas) {
            renderPdfPage.call(self, pageNum, null, canvas);
        }
    });
}

/**
 * Scrolls the PDF container to bring a specific page into view (continuous scroll mode).
 * Called with `this` bound to a Viewport instance.
 * @param {number} pageNum - 1-based page number
 */
export function scrollToPdfPage(pageNum) {
    const self = this;
    if (!self.pdfDoc || self._pdfScrollMode !== "continuous" || !self._pdfPageWrappers) {
        return;
    }

    const wrapper = self._pdfPageWrappers.get(pageNum);
    if (wrapper && self.pdfContainer) {
        self.pdfContainer.scrollTop = wrapper.offsetTop - 8;
    }
}

/**
 * Creates an integrated PDF viewer inside the viewport element, bypassing the
 * OpenLayers map entirely.  Called with `this` bound to a Viewport instance.
 *
 * Reads the following options from `this.options`:
 * - `pdfScrollMode` {string} `"continuous"` (default) | `"single"` — layout mode.
 * - `pdfZoom` {number} Initial zoom multiplier (default `1.0`).
 */
export function createPdfViewer() {
    const self = this;

    // ── Normalise imageInfo fields that may be absent for PDF ────────────────
    if (!this.imageInfo.TimeFrames) {
        this.imageInfo.TimeFrames = [{ TimeID: 0, Layers: [{ LayerID: 0, Channels: [] }] }];
    }

    if (!this.imageInfo.AssociatedImageTypes) {
        this.imageInfo.AssociatedImageTypes = [];
    }

    if (this.imageInfo.MicrometresPerPixelX == null) {
        this.imageInfo.MicrometresPerPixelX = 0;
    }

    if (this.imageInfo.MicrometresPerPixelY == null) {
        this.imageInfo.MicrometresPerPixelY = 0;
    }

    this.channelsString = "";
    this.channelClippingString = "";
    this.channelColorString = "";
    this.channelGammaString = "";
    this.renderingOptionsCallback = null;

    // ── Apply element CSS classes (same pattern as createOlViewer) ───────────
    addClass(this.element, "pma-ui-viewport-container");
    for (var key in Themes) {
        if (!Object.prototype.hasOwnProperty.call(Themes, key)) {
            continue;
        }

        removeClass(this.element, Themes[key]);
    }

    addClass(this.element, this.options.theme);

    this.element.innerHTML = "";
    var bgColor = this.imageInfo.BackgroundColor ? "#" + this.imageInfo.BackgroundColor : "#525659";
    this.element.style.backgroundColor = bgColor;

    // ── PDF-specific state ───────────────────────────────────────────────────
    const scrollMode = (this.options.pdf.scrollMode === "single") ? "single" : "continuous";
    this._pdfScrollMode   = scrollMode;
    this._pdfZoomFactor   = (typeof this.options.pdf.zoom === "number" && this.options.pdf.zoom > 0)
        ? this.options.pdf.zoom : 1.0;
    this._pdfCurrentPage  = 1;
    this._pdfScale        = 1.0;
    this._pdfRenderTask   = null;
    // Use window.Map to avoid collision with the `Map` imported from "ol"
    this._pdfPageCanvases = new window.Map();   // pageNum → <canvas>  (continuous mode)
    this._pdfPageWrappers = new window.Map();   // pageNum → wrapper div (continuous mode)

    // ── Outer scrollable container ───────────────────────────────────────────
    var container = document.createElement("div");
    container.className = "pma-ui-pdf-container";
    this.pdfContainer = container;

    // ── Canvas (single-page mode only) ───────────────────────────────────────
    var canvas = null;
    if (scrollMode === "single") {
        canvas = document.createElement("canvas");
        canvas.className = "pma-ui-pdf-canvas";
        container.appendChild(canvas);
    }

    this.pdfCanvas = canvas;

    // ── Resolve filename ─────────────────────────────────────────────────────
    var fileName = null;
    if (typeof this.options.filename === "string") {
        fileName = this.options.filename;
    } else if (typeof this.options.filename === "function") {
        fileName = this.options.filename.call(this, { serverUrl: this.getActiveServerUrl(), path: this.imageInfo.Filename });
    } else {
        fileName = this.imageInfo.Filename;
    }

    this.element.appendChild(container);

    // ── Navigation / zoom controls ───────────────────────────────────────────
    var navResult = _initNavControls.call(this, scrollMode);
    // Store on instance so scroll-based page tracking can update button states.
    this._pdfUpdateNavButtons = navResult.updateNavButtons;
    if (this.options.pdf.navigationControls !== false) {
        this.element.appendChild(navResult.controls);
    }

    // ── Filename bar ─────────────────────────────────────────────────────────
    if (this.options.filename !== false) {
        _initFilenameBar.call(this, fileName);
    }

    // ── Top-right action bar ─────────────────────────────────────────────────
    _initTopBar.call(this);

    // ── Container interactions (Ctrl+wheel zoom, LMB pan) ────────────────────
    _initContainerInteraction(self, container, navResult.applyZoom);

    // ── Initial disabled state ───────────────────────────────────────────────
    navResult.prevBtn.disabled = true;
    navResult.nextBtn.disabled = true;

    // ── Load PDF ─────────────────────────────────────────────────────────────
    var pdfUrl = getPdfUrl.call(self);

    pdfjs.getDocument(pdfUrl).promise
        .then(function (pdfDoc) {
            self.pdfDoc = pdfDoc;
            self._pdfCurrentPage = 1;
            navResult.updateNavButtons();

            var _pdfLoadStart = performance.now();
            if (scrollMode === "continuous") {
                _buildContinuousPages.call(self, pdfDoc, function () {
                    if (typeof self.readyCallback === "function") {
                        self.readyCallback();
                    }

                    self.fireEvent(Events.ViewLoaded, { time: performance.now() - _pdfLoadStart, tiles: pdfDoc.numPages });
                });
            } else {
                renderPdfPage.call(self, 1, function () {
                    if (typeof self.readyCallback === "function") {
                        self.readyCallback();
                    }

                    self.fireEvent(Events.ViewLoaded, { time: performance.now() - _pdfLoadStart, tiles: pdfDoc.numPages });
                });
            }
        })
        .catch(function (err) {
            console.error("PDF load error:", err);
            if (typeof self.failCallback === "function") {
                self.failCallback();
            }

            self.fireEvent(Events.SlideLoadError, self);
        });

    // ── Page navigation ───────────────────────────────────────────────────────
    navResult.prevBtn.addEventListener("click", function () {
        if (self._pdfCurrentPage > 1) {
            self._pdfCurrentPage--;
            if (scrollMode === "continuous") {
                scrollToPdfPage.call(self, self._pdfCurrentPage);
            } else {
                renderPdfPage.call(self, self._pdfCurrentPage, function () {
                    if (self.pdfContainer) {
                        self.pdfContainer.scrollTop = 0;
                    }
                });
            }

            _updatePdfPageInfo.call(self);
            navResult.updateNavButtons();
            self.fireEvent(Events.ViewChanged, self);
        }
    });

    navResult.nextBtn.addEventListener("click", function () {
        if (self.pdfDoc && self._pdfCurrentPage < self.pdfDoc.numPages) {
            self._pdfCurrentPage++;
            if (scrollMode === "continuous") {
                scrollToPdfPage.call(self, self._pdfCurrentPage);
            } else {
                renderPdfPage.call(self, self._pdfCurrentPage, function () {
                    if (self.pdfContainer) {
                        self.pdfContainer.scrollTop = 0;
                    }
                });
            }

            _updatePdfPageInfo.call(self);
            navResult.updateNavButtons();
            self.fireEvent(Events.ViewChanged, self);
        }
    });
}

/**
 * Creates the navigation controls bar (prev/next page, page info, zoom in/out).
 * Called with `this` bound to a Viewport instance.
 * @private
 * @param {string} scrollMode - "continuous" or "single"
 * @returns {{ controls: HTMLElement, prevBtn: HTMLButtonElement, nextBtn: HTMLButtonElement, updateNavButtons: Function, applyZoom: Function }}
 */
function _initNavControls(scrollMode) {
    const self = this;
    const ZOOM_STEP = 0.125;
    const ZOOM_MIN  = 0.25;
    const ZOOM_MAX  = 2.0;

    var controls = document.createElement("div");
    controls.className = "pma-ui-pdf-navigation-controls ol-unselectable";

    var prevBtn = document.createElement("button");
    prevBtn.type = "button"; prevBtn.innerHTML = "&#8249;"; prevBtn.title = "Previous page";
    prevBtn.className = "pma-ui-pdf-btn";

    var pageSpan = document.createElement("span");
    pageSpan.className = "pma-ui-pdf-page-info";
    pageSpan.textContent = "Loading\u2026";
    this.pdfPageInfo = pageSpan;

    var nextBtn = document.createElement("button");
    nextBtn.type = "button"; nextBtn.innerHTML = "&#8250;"; nextBtn.title = "Next page";
    nextBtn.className = "pma-ui-pdf-btn";

    var sep = document.createElement("span");
    sep.className = "pma-ui-pdf-sep";

    var zoomOutBtn = document.createElement("button");
    zoomOutBtn.type = "button"; zoomOutBtn.textContent = "\u2212"; zoomOutBtn.title = "Zoom out";
    zoomOutBtn.className = "pma-ui-pdf-zoom-btn";

    var zoomLabel = document.createElement("span");
    zoomLabel.className = "pma-ui-pdf-zoom-label";
    zoomLabel.title = "Reset zoom";
    this._pdfZoomLabel = zoomLabel;

    var zoomInBtn = document.createElement("button");
    zoomInBtn.type = "button"; zoomInBtn.textContent = "+"; zoomInBtn.title = "Zoom in";
    zoomInBtn.className = "pma-ui-pdf-zoom-btn";

    function updateZoomLabel() {
        zoomLabel.textContent = Math.round(self._pdfZoomFactor * 100) + "%";
    }

    updateZoomLabel();

    function updateNavButtons() {
        prevBtn.disabled = self._pdfCurrentPage <= 1;
        nextBtn.disabled = !self.pdfDoc || self._pdfCurrentPage >= self.pdfDoc.numPages;
    }

    function updateZoomButtons() {
        zoomOutBtn.disabled = self._pdfZoomFactor <= ZOOM_MIN;
        zoomInBtn.disabled  = self._pdfZoomFactor >= ZOOM_MAX;
    }

    updateZoomButtons();

    function applyZoom(newZoom) {
        const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom));
        if (clamped === self._pdfZoomFactor) {
            return;
        }

        self._pdfZoomFactor = clamped;
        updateZoomLabel();
        updateZoomButtons();
        if (scrollMode === "continuous") {
            refreshPdfPages.call(self);
        } else {
            renderPdfPage.call(self, self._pdfCurrentPage);
        }

        self.fireEvent(Events.ViewChanged, self);
    }

    zoomOutBtn.addEventListener("click", function () {
        applyZoom(self._pdfZoomFactor - ZOOM_STEP);
    });

    zoomInBtn.addEventListener("click",  function () {
        applyZoom(self._pdfZoomFactor + ZOOM_STEP);
    });

    zoomLabel.addEventListener("click",  function () {
        applyZoom(1.0);
    });

    controls.appendChild(prevBtn);
    controls.appendChild(pageSpan);
    controls.appendChild(nextBtn);
    controls.appendChild(sep);
    controls.appendChild(zoomOutBtn);
    controls.appendChild(zoomLabel);
    controls.appendChild(zoomInBtn);

    return { controls, prevBtn, nextBtn, updateNavButtons, applyZoom };
}

/**
 * Creates the collapsible filename bar and appends it to the viewport element.
 * Called with `this` bound to a Viewport instance.
 * @private
 * @param {string} fileName
 */
function _initFilenameBar(fileName) {
    const self = this;

    var fnBar = document.createElement("div");
    fnBar.className = "pma-ui-pdf-filename ol-unselectable";

    var fnToggle = document.createElement("button");
    fnToggle.type = "button";
    fnToggle.className = "pma-ui-pdf-filename-toggle";
    fnToggle.title = "Toggle filename";
    fnToggle.innerHTML = '\u00AB'; // «

    var fnText = document.createElement("span");
    fnText.className = "pma-ui-pdf-filename-text";
    fnText.textContent = fileName;

    // Respect the same collapsed option as the OL Filename control
    var _fnCollapsed = false;
    if (this.options.filename && typeof this.options.filename === "object" && this.options.filename.collapsed === true) {
        _fnCollapsed = true;
    } else if (this.options.filename && typeof this.options.filename !== "string" && typeof this.options.filename !== "function" && this.options.filename !== true) {
        _fnCollapsed = !!(this.options.filename && this.options.filename.collapsed);
    }

    if (_fnCollapsed) {
        addClass(fnBar, "ol-collapsed");
        fnToggle.innerHTML = '\u00BB';
    }

    fnToggle.addEventListener("click", function () {
        if (fnBar.className.indexOf("ol-collapsed") > -1) {
            removeClass(fnBar, "ol-collapsed");
            fnToggle.innerHTML = '\u00AB';
        } else {
            addClass(fnBar, "ol-collapsed");
            fnToggle.innerHTML = '\u00BB';
        }
    });

    fnText.addEventListener("click", function () {
        self.fireEvent(Events.FilenameClick, self);
    });

    fnBar.appendChild(fnToggle);
    fnBar.appendChild(fnText);
    this.element.appendChild(fnBar);
    this.pdfFilenameBar = fnBar;
}

/**
 * Creates the top-right action bar (open-in-new-tab + fullscreen toggle) and
 * appends it to the viewport element.
 * Called with `this` bound to a Viewport instance.
 * @private
 */
function _initTopBar() {
    const self = this;

    var topBar = document.createElement("div");
    topBar.className = "pma-ui-pdf-topbar ol-unselectable";

    var _svgOpenTab = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>';
    var _svgFsEnter = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>';
    var _svgFsExit  = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>';

    var openTabBtn = document.createElement("button");
    openTabBtn.type = "button";
    openTabBtn.innerHTML = _svgOpenTab;
    openTabBtn.title = "Open in new tab";
    openTabBtn.className = "pma-ui-pdf-topbar-btn";
    openTabBtn.addEventListener("click", function () {
        window.open(getPdfUrl.call(self), "_blank");
    });

    var fsBtn = document.createElement("button");
    fsBtn.type = "button";
    fsBtn.className = "pma-ui-pdf-topbar-btn";
    var _fsTarget = (self.options.fullScreenElement instanceof HTMLElement)
        ? self.options.fullScreenElement : self.element;

    function _pdfFsActive() {
        return !!(document.fullscreenElement || document.webkitFullscreenElement ||
                  document.mozFullScreenElement || document.msFullscreenElement);
    }

    function _pdfSyncFsBtn() {
        var active = _pdfFsActive();
        fsBtn.innerHTML = active ? _svgFsExit : _svgFsEnter;
        fsBtn.title = active ? "Exit fullscreen" : "Toggle fullscreen";
    }

    _pdfSyncFsBtn();

    fsBtn.addEventListener("click", function () {
        if (_pdfFsActive()) {
            var exitFn = document.exitFullscreen || document.webkitExitFullscreen ||
                         document.mozCancelFullScreen || document.msExitFullscreen;
            if (exitFn) {
                exitFn.call(document);
            }
        } else {
            var enterFn = _fsTarget.requestFullscreen || _fsTarget.webkitRequestFullscreen ||
                          _fsTarget.mozRequestFullScreen || _fsTarget.msRequestFullscreen;
            if (enterFn) {
                enterFn.call(_fsTarget);
            }
        }
    });

    var _pdfFsHandler = function () {
        _pdfSyncFsBtn();
        // Re-render after the fullscreen transition so pages fit the new container size.
        // A short delay is needed because the element dimensions are not updated
        // synchronously when the fullscreenchange event fires.
        setTimeout(function () {
            if (!self.pdfDoc) {
                return;
            }

            if (self._pdfScrollMode === "continuous") {
                refreshPdfPages.call(self);
            } else {
                renderPdfPage.call(self, self._pdfCurrentPage);
            }
        }, 150);
    };

    document.addEventListener("fullscreenchange",       _pdfFsHandler);
    document.addEventListener("webkitfullscreenchange", _pdfFsHandler);
    document.addEventListener("mozfullscreenchange",    _pdfFsHandler);
    document.addEventListener("MSFullscreenChange",     _pdfFsHandler);

    if (self.options.fullscreenControl !== false) {
        topBar.appendChild(fsBtn);
    }

    if (this.options.pdf.openInNewTabControl !== false) {
        topBar.appendChild(openTabBtn);
    }

    this.element.appendChild(topBar);
}

/**
 * Attaches Ctrl+wheel zoom and left-mouse-button pan listeners to the PDF
 * scroll container.
 * @private
 * @param {Object} self - Viewport instance
 * @param {HTMLElement} container - The PDF scroll container
 * @param {Function} applyZoom - Zoom applicator returned by _initNavControls
 */
function _initContainerInteraction(self, container, applyZoom) {
    const ZOOM_STEP = 0.125;

    // Ctrl + mouse-wheel zoom
    container.addEventListener("wheel", function (e) {
        if (!e.ctrlKey) {
            return;
        }

        e.preventDefault();
        var delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        applyZoom(self._pdfZoomFactor + delta);
    }, { passive: false });

    // Left-mouse-button pan
    var _panStartX = 0;
    var _panStartY = 0;
    var _panScrollLeft = 0;
    var _panScrollTop  = 0;
    var _panning = false;

    container.addEventListener("mousedown", function (e) {
        if (e.button !== 0) {
            return;
        }

        _panning = true;
        _panStartX     = e.clientX;
        _panStartY     = e.clientY;
        _panScrollLeft = container.scrollLeft;
        _panScrollTop  = container.scrollTop;
        container.style.cursor = "grabbing";
        container.style.userSelect = "none";
        e.preventDefault();
    });

    container.addEventListener("mousemove", function (e) {
        if (!_panning) {
            return;
        }

        container.scrollLeft = _panScrollLeft - (e.clientX - _panStartX);
        container.scrollTop  = _panScrollTop  - (e.clientY - _panStartY);
    });

    function endPan() {
        if (!_panning) {
            return;
        }

        _panning = false;
        container.style.cursor = "";
        container.style.userSelect = "";
    }

    container.addEventListener("mouseup",    endPan);
    container.addEventListener("mouseleave", endPan);
}

/**
 * Builds one canvas wrapper per page for continuous-scroll mode and starts
 * lazy rendering via IntersectionObserver.
 * @private
 */
function _buildContinuousPages(pdfDoc, allRenderedCallback) {
    const self = this;
    const numPages = pdfDoc.numPages;
    let firstPageReady = false;

    // Disconnect any previous observer
    if (self._pdfPageObserver) {
        self._pdfPageObserver.disconnect();
        self._pdfPageObserver = null;
    }

    // Clear previous page elements
    self._pdfPageCanvases.clear();
    self._pdfPageWrappers.clear();

    // IntersectionObserver: render a page when it enters the viewport
    const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
                return;
            }

            const pageNum = parseInt(entry.target.dataset.pdfPage, 10);
            if (!self._pdfPageCanvases.has(pageNum)) {
                return;
            }

            const canvas = self._pdfPageCanvases.get(pageNum);
            if (canvas.dataset.rendered === "1") {
                return;
            }

            canvas.dataset.rendered = "1";

            renderPdfPage.call(self, pageNum, function () {
                if (!firstPageReady && pageNum === 1) {
                    firstPageReady = true;
                    if (typeof allRenderedCallback === "function") {
                        allRenderedCallback();
                    }
                }
            }, canvas);
        });
    }, {
        root: self.pdfContainer,
        rootMargin: "200px 0px",
        threshold: 0,
    });
    self._pdfPageObserver = observer;

    // Scroll listener to track the current visible page
    self.pdfContainer.addEventListener("scroll", function () {
        _updateCurrentPageFromScroll.call(self);
    });

    // Build placeholder wrappers for all pages so the scrollbar reflects true
    // document height even before pages are rendered.
    const containerWidth = Math.max(self.pdfContainer.clientWidth - 32, 100);

    var pending = numPages;
    for (var p = 1; p <= numPages; p++) {
        (function (pageNum) {
            pdfDoc.getPage(pageNum).then(function (page) {
                const unscaledViewport = page.getViewport({ scale: 1 });
                const baseScale = containerWidth / unscaledViewport.width;
                const scaledHeight = Math.round(unscaledViewport.height * baseScale * (self._pdfZoomFactor || 1.0));
                const scaledWidth  = Math.round(unscaledViewport.width  * baseScale * (self._pdfZoomFactor || 1.0));

                const wrapper = document.createElement("div");
                wrapper.className = "pma-ui-pdf-page-wrapper";
                wrapper.dataset.pdfPage = String(pageNum);
                wrapper.style.width = scaledWidth + "px";
                wrapper.style.minHeight = scaledHeight + "px";

                const canvas = document.createElement("canvas");
                canvas.className = "pma-ui-pdf-canvas";
                wrapper.appendChild(canvas);

                self._pdfPageCanvases.set(pageNum, canvas);
                self._pdfPageWrappers.set(pageNum, wrapper);

                // Insert in order
                const children = self.pdfContainer.querySelectorAll(".pma-ui-pdf-page-wrapper");
                var inserted = false;
                for (var i = 0; i < children.length; i++) {
                    if (parseInt(children[i].dataset.pdfPage, 10) > pageNum) {
                        self.pdfContainer.insertBefore(wrapper, children[i]);
                        inserted = true;
                        break;
                    }
                }
            
                if (!inserted) {
                    self.pdfContainer.appendChild(wrapper);
                }

                observer.observe(wrapper);

                pending--;
                if (pending === 0) {
                    // Trigger initial observation pass
                    _updatePdfPageInfo.call(self);
                }
            });
        })(p);
    }

    // Fire callback immediately if numPages is 0 (edge case)
    if (numPages === 0 && typeof allRenderedCallback === "function") {
        allRenderedCallback();
    }
}

/**
 * Updates `_pdfCurrentPage` based on scroll position and refreshes the page info label.
 * @private
 */
function _updateCurrentPageFromScroll() {
    const self = this;
    if (!self._pdfPageWrappers || !self.pdfContainer) {
        return;
    }

    const scrollTop  = self.pdfContainer.scrollTop;
    const clientHeight = self.pdfContainer.clientHeight;
    const center     = scrollTop + clientHeight / 2;

    let closest = 1;
    let closestDist = Infinity;

    self._pdfPageWrappers.forEach(function (wrapper, pageNum) {
        const mid = wrapper.offsetTop + wrapper.offsetHeight / 2;
        const dist = Math.abs(mid - center);
        if (dist < closestDist) {
            closestDist = dist;
            closest = pageNum;
        }
    });

    if (closest !== self._pdfCurrentPage) {
        self._pdfCurrentPage = closest;
        _updatePdfPageInfo.call(self);
        if (typeof self._pdfUpdateNavButtons === "function") {
            self._pdfUpdateNavButtons();
        }
        
        self.fireEvent(Events.ViewChanged, self);
    }
}

/**
 * Updates the page info span text.
 * @private
 */
function _updatePdfPageInfo() {
    if (this.pdfPageInfo && this.pdfDoc) {
        this.pdfPageInfo.textContent = this._pdfCurrentPage + " / " + this.pdfDoc.numPages;
    }
}
